import { useState, useRef, useEffect } from 'react';
import { ChevronRight } from '@carespace-icons/arrows/chevronRight';
import { ChevronLeft } from '@carespace-icons/arrows/chevronLeft';
import {
	Card,
	Button,
	Typography,
	Flex,
	Checkbox,
	Steps,
	message,
	Spin,
	Modal,
	Empty,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTypedDispatch, useTypedSelector } from '@stores/index';
import { Calendar } from '@carespace-icons/time/calendar';
import './style.css';
import InitialCard from './IntakeProgramsInitialCard';
import BodyWithHotspots from './IntakeProgramsBody';
import { getMySystemIntakeTemplatesData } from '@stores/functionalGoals';
import {
	FormDataType,
	IProgramFilterOptions,
	ISelectedProgramModalData,
	ISuggestedProgramItem,
} from '@stores/interfaces';
import { setCurrentStep } from '@stores/onBoard/onBoard';
import { INTAKE_STEPS } from '@stores/constants';
import SelectedProgram from './SelectedProgram';
import { createProgram } from '@stores/aiAssistant/program';
const { Title } = Typography;

export default function SuggestedPrograms(props: {
	current: number;
	fetchHomeData: (value: number) => void;
	setCurrent: (value: number) => void;
	suggestedProgramData: ISuggestedProgramItem[];
	currentPage: number;
	setCurrentPage: (value: (prev: number) => number) => void;
}) {
	const {
		suggestedProgramData,
		currentPage,
		setCurrentPage,
		current,
		setCurrent,
		fetchHomeData,
	} = props;
	const [formData, setFormData] = useState<FormDataType>({
		goalSelection: null,
		experienceLevel: null,
		workoutPreference: null,
		timeAvailability: null,
	});
	const { selectedUser } = useTypedSelector(state => state.contacts.main);
	const [activeImage, setActiveImage] = useState<number | null>(null);
	const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);
	const [suggestedProgramModal, setSuggestedProgramModal] = useState(false);
	const scrollRef = useRef<HTMLDivElement | null>(null);
	const [isLoading, setLoading] = useState(true);
	const { t } = useTranslation();
	const dispatch = useTypedDispatch();
	const navigate = useNavigate();
	const [suggestedProgram, setSuggestedProgram] = useState<
		ISuggestedProgramItem[]
	>([]);
	const [suggestedProgramPageCount, setSuggestedProgramPageCount] = useState(0);
	const scrollLeft = () => {
		if (scrollRef.current) {
			const currentPosition = scrollRef.current?.scrollLeft;
			const targetPosition = currentPosition - 400;
			scrollToPosition(targetPosition, 300);
		}
	};
	const user = useTypedSelector(state => state.user);
	const scrollRight = () => {
		if (scrollRef.current) {
			const currentPosition = scrollRef.current?.scrollLeft;
			const targetPosition = currentPosition + 400;
			scrollToPosition(targetPosition, 300);
		}
		if (
			scrollRef.current &&
			scrollRef.current.scrollLeft + scrollRef.current.offsetWidth >=
				scrollRef.current.scrollWidth
		) {
			handleNextPage();
		}
	};

	const [selectedProgramModalData, setSelectedProgramModalData] =
		useState<ISelectedProgramModalData>({
			selectedProgram: {} as ISuggestedProgramItem,
			domain: '',
		});

	const [isSelectAll, setIsSelectAll] = useState(false);

	const handleOpenProgram = (item: ISuggestedProgramItem) => {
		setSelectedProgramModalData({
			selectedProgram: item,
			domain: INTAKE_STEPS.STEP_PROGRAM,
		});
	};

	const [filterOptions, setFilterOptions] = useState<IProgramFilterOptions>({
		goalSelection: '',
		bodyRegions: [],
		workoutType: '',
		experienceLevel: '',
		timeAvailability: '',
		bodyRegionIds: [],
		exerciseCategoriesIds: [],
		jointsIds: [],
		functionalGoalsIds: [],
		sideIds: [],
	});

	const handleNextPage = () => {
		setCurrentPage((prev: number) => prev + 1);
	};

	useEffect(() => {
		if (suggestedProgram && suggestedProgram.length > 0) {
			currentPage <= suggestedProgramPageCount && current === 2
				? fetchData()
				: message.warning(t('Admin.data.intakeProcess.reachedEnd'));
		}
	}, [currentPage]);

	const scrollToPosition = (targetPosition: number, duration: number) => {
		const startTime = performance.now();
		const start = scrollRef.current?.scrollLeft;

		const scroll = (timestamp: number) => {
			const elapsed = timestamp - startTime;
			const progress = Math.min(elapsed / duration, 1);
			if (scrollRef.current) {
				scrollRef.current.scrollLeft =
					start! + (targetPosition - start!) * progress;
			}
			if (progress < 1) {
				requestAnimationFrame(scroll);
			}
		};

		requestAnimationFrame(scroll);
	};

	const handleImageClick = (index: number, videoUrl: string) => {
		setActiveImage(index);
		setActiveVideoUrl(videoUrl);
	};

	const handleEnroll = async (item: ISuggestedProgramItem) => {
		const exercises: {
			strapiExerciseId: string;
			exerciseLibraryId: string;
			weeklyReps: number;
			dailyReps: number;
			sets: number;
			reps: number;
			order: number;
		}[] = [];
		item?.exercises?.forEach((exercise, index: number) => {
			const tempExercise = {
				strapiExerciseId: String(
					typeof exercise?.strapiExerciseId === 'object'
						? exercise?.strapiExerciseId?.id
						: exercise?.strapiExerciseId || '',
				),
				exerciseLibraryId: exercise.exerciseLibraryId || '',
				weeklyReps: exercise.weeklyReps,
				dailyReps: exercise.dailyReps,
				sets: exercise.sets,
				reps: exercise.reps,
				order: index + 1,
			};
			exercises.push(tempExercise);
		});
		const formData = new FormData();
		formData.append('name', item?.name);
		formData.append('duration', item?.duration.toString());
		formData.append('durationType', item?.durationType);
		formData.append('description', item?.description);
		formData.append(
			'thumbnail',
			typeof item?.thumbnail === 'string'
				? item?.thumbnail
				: item?.thumbnail?.url || '',
		);
		if (user.isPhysioterapist ? selectedUser?.id : user?.id) {
			formData.append(
				'userId',
				String(user.isPhysioterapist ? selectedUser?.id : user?.id),
			);
		} else {
			console.error('userId is missing or invalid');
			return;
		}
		formData.append('strapiProgramTemplateId', item?.id);
		exercises?.forEach((exercise, index) => {
			Object.keys(exercise).forEach(key => {
				if (exercise[key as keyof typeof exercise]) {
					formData.append(
						`exercises[create][${index}][${key}]`,
						exercise[key as keyof typeof exercise] as string,
					);
				}
			});
		});
		const data = await dispatch(createProgram(formData));
		if (data?.payload?.id) {
			message.destroy();
			message.success(t('Patient.data.newDashboard.enrolled'));
			fetchHomeData(1);
		}
	};

	const currentStep = useTypedSelector(
		state => state.onBoard.onBoard.currentStep,
	);

	const [selectedItems, setSelectedItems] = useState<ISuggestedProgramItem[]>(
		[],
	);

	const handleCheckboxChange = (
		item: ISuggestedProgramItem,
		isChecked: boolean,
	) => {
		setIsSelectAll(false);

		setSelectedItems(prev => {
			if (isChecked) {
				if (!prev.includes(item)) {
					return [...prev, item];
				}
				return prev;
			} else {
				return prev.filter(selectedItem => selectedItem !== item);
			}
		});
	};

	const handleSubmitSelected = () => {
		dispatch(setCurrentStep(INTAKE_STEPS.SUGGESTED));
		selectedItems.forEach(item => {
			handleEnroll(item);
		});
		setCurrent(0);
		setSelectedItems([]);
		setIsSelectAll(false);
	};

	const [selectedDropdown, setSelectedDropdown] = useState('');

	const steps = [
		{
			title: t('Admin.data.intakeProcess.welcome'),
			content: <InitialCard />,
		},
		{
			title: t('Admin.data.intakeProcess.region'),
			content: (
				<BodyWithHotspots
					selectedDropdown={selectedDropdown}
					setSelectedDropdown={setSelectedDropdown}
				/>
			),
		},
		// {
		// 	title: t('Admin.data.intakeProcess.goals'),
		// 	content: (
		// 		<GoalSelectionCard
		// 		formData={formData}
		// 		setFormData={setFormData}
		// 			selectedGoal={selectedGoal}
		// 			handleGetStep={handleGetStep}
		// 			onGoalChange={setSelectedGoal}
		// 			filterOptions={filterOptions}
		// 			setFilterOptions={setFilterOptions}
		// 		/>
		// 	),
		// },
		{
			title: t('Admin.data.intakeProcess.programs'),
			content: (
				<>
					<Flex
						justify="center"
						vertical
						style={{ height: '100%', padding: '80px 20px' }}>
						<Title
							level={2}
							style={{ color: '#fff', marginBottom: 24, textAlign: 'center' }}>
							{t('Admin.data.intakeProcess.programMatch')}
						</Title>
						{isLoading ? (
							<Spin
								className="spin-css spin-css-inbox"
								tip="Loading"
								size="large"
							/>
						) : suggestedProgram.length > 0 ? (
							<div className="image-navigation-container">
								<div
									className="horizontal-scroll-container relative"
									ref={scrollRef}>
									{suggestedProgram?.map(
										(item: ISuggestedProgramItem, index: number) => {
											const videoUrl =
												item?.attributes?.exercise_video?.data[0]?.attributes
													?.url;
											const selected = selectedItems.filter(
												selected => selected.id === item.id,
											);
											return (
												<div
													key={index}
													className={`custom-image-container-form ${activeImage === index ? 'active-image' : ''}`}
													onMouseOver={() => handleImageClick(index, videoUrl)}
													onMouseLeave={() => setActiveImage(null)}
													onClick={() => {
														handleOpenProgram(item);
														setSuggestedProgramModal(true);
													}}>
													<div className="image-wrapper-form">
														{item?.exercises![0] ? (
															<>
																<img
																	src={
																		item?.thumbnail
																			? typeof item.thumbnail === 'string'
																				? item.thumbnail
																				: item.thumbnail.url
																			: item?.exercises[0]?.strapiExerciseId
																					?.exercise_image[0]?.url
																	}
																	alt={
																		item?.exercises[0]?.strapiExerciseId?.name
																	}
																	className="program-image-container-custom-class saturate-image"
																/>
																<div
																	className="primary-goal-list"
																	style={{
																		position: 'absolute',
																		right: '10px',
																		top: '10px',
																		zIndex: 1,
																	}}>
																	<Checkbox
																		value={item}
																		checked={selected.length > 0}
																		onClick={e => e.stopPropagation()}
																		onChange={e => {
																			e.stopPropagation();
																			handleCheckboxChange(
																				item,
																				e.target.checked,
																			);
																		}}
																	/>
																</div>
															</>
														) : (
															<video
																preload="metadata"
																src={
																	item?.exercises![0]?.exerciseLibrary?.videoUrl
																}
																className="program-image-container-custom-class"
															/>
														)}
														<div className="overlay-content-css">
															<p
																className={`program-text-container-custom-class ${activeImage === index ? 'mb-2' : ''}`}>
																{item?.name}
															</p>
															{item?.exercises && (
																<>
																	<Flex justify="space-between">
																		<div className="exercise-count">
																			{`${item?.exercises.length} ${t(
																				`Patient.data.newDashboard.${item?.exercises.length === 1 ? 'exercise' : 'exercises'}`,
																			)}`}
																		</div>
																		<div
																			className="exercise-count-duration"
																			style={{
																				display: 'flex',
																				gap: '0.25rem',
																			}}>
																			<Calendar height={20} width={20} />
																			{item?.duration}{' '}
																			{item?.durationType
																				? item?.duration > 1
																					? item.durationType
																							.charAt(0)
																							.toUpperCase() +
																						item.durationType.slice(1)
																					: item.durationType
																							.charAt(0)
																							.toUpperCase() +
																						item.durationType
																							.slice(1)
																							.replace(/s$/, '')
																				: ''}
																		</div>
																	</Flex>
																</>
															)}
														</div>
													</div>
												</div>
											);
										},
									)}
								</div>
								<div className="navigation-buttons">
									<span className="left-program-arrow-btn" onClick={scrollLeft}>
										<ChevronLeft width={30} height={30} />
									</span>
									<span
										className="right-program-arrow-btn"
										onClick={scrollRight}>
										<ChevronRight width={30} height={30} />
									</span>
								</div>
							</div>
						) : (
							<Empty
								image={Empty.PRESENTED_IMAGE_SIMPLE}
								className="suggested-intake"
							/>
						)}
					</Flex>
				</>
			),
		},
	];

	const id = useTypedSelector(state => state.user.id);

	const fetchData = async () => {
		const payload = {
			id: id,
			limit: 10,
			page: currentPage,
			title: '',
			searchValue: '',
			filterOptions: {
				bodyRegionIds: [],
				exerciseCategoriesIds: [],
				sideIds: [],
				bodyRegions: [],
				jointsIds: selectedDropdown,
				workoutType: formData.workoutPreference,
				timeAvailability: formData.timeAvailability,
				experienceLevel: formData.experienceLevel,
				goalSelection: formData.goalSelection,
				functionalGoalsIds: filterOptions?.functionalGoalsIds,
			},
		};
		try {
			currentPage === 1 && setLoading(true);
			const response = await dispatch(
				getMySystemIntakeTemplatesData(payload),
			).unwrap();

			const fetchedData = response.data;
			const paginationData = response.meta.pagination.pageSize;

			if (currentPage === 1) {
				setSuggestedProgram(fetchedData);
				setSuggestedProgramPageCount(paginationData);
			} else {
				setSuggestedProgram([...suggestedProgram, ...fetchedData]);
				setSuggestedProgramPageCount(paginationData);
			}
		} catch (error) {
			console.error('Error fetching system templates:', error);
		}
		setLoading(false);
	};

	const next = () => {
		current === 1 && fetchData();
		setCurrent(current + 1);
	};

	const prev = () => {
		current === 2 && setCurrentPage(() => 1);
		setCurrent(current - 1);
	};

	useEffect(() => {
		if (suggestedProgram.length > 0) {
			if (selectedItems.length === suggestedProgram.length) {
				setIsSelectAll(true);
			} else setIsSelectAll(false);
		}
	}, [suggestedProgram, selectedItems]);

	useEffect(() => {
		if (current === 0) {
			setSelectedDropdown('');
			setFilterOptions({
				goalSelection: '',
				bodyRegions: [],
				workoutType: '',
				experienceLevel: '',
				timeAvailability: '',
				bodyRegionIds: [] as number[],
				exerciseCategoriesIds: [] as number[],
				jointsIds: [] as number[],
				functionalGoalsIds: [] as number[],
				sideIds: [] as number[],
			});
			setFormData({
				goalSelection: null,
				experienceLevel: null,
				workoutPreference: null,
				timeAvailability: null,
			});
			setSuggestedProgram([]);
		}
	}, [current]);

	if (currentStep === '') {
		return <Spin className="spin-css" tip="Loading" size="large" />;
	}

	const handleSelectAll = () => {
		const selectedData: ISuggestedProgramItem[] = [];
		suggestedProgram.forEach(item => {
			if (!isSelectAll) {
				selectedData.push(item);
			}
		});
		setSelectedItems(selectedData);
		setIsSelectAll(!isSelectAll);
	};

	return (
		<>
			<div>
				{currentStep === INTAKE_STEPS.INTAKE_PROGRAMS && (
					<Card
						className={`${current === 0 ? 'intake-programs' : 'goal-selection-card'}`}>
						<Steps
							style={{
								position: 'absolute',
								top: '20px',
								left: '20px',
								width: '97%',
								padding: '10px',
								borderRadius: '12px',
							}}
							current={current}
							items={steps}
						/>
						<div style={{ height: '100%' }}>{steps[current].content}</div>
						<div
							style={{
								marginTop: 24,
								position: 'absolute',
								bottom: '15px',
								left: '25px',
							}}>
							<Button
								className="skip"
								onClick={() => {
									dispatch(setCurrentStep(INTAKE_STEPS.SUGGESTED));
									setCurrent(0);
								}}>
								{t('Admin.data.intakeProcess.skip')}
							</Button>
						</div>
						<div
							style={{
								marginTop: 24,
								position: 'absolute',
								bottom: '15px',
								right: '25px',
							}}>
							{current > 0 && (
								<Button style={{ margin: '0 8px' }} onClick={prev}>
									{t('Admin.data.intakeProcess.previous')}
								</Button>
							)}
							{current < steps.length - 1 && (
								<Button
									onClick={next}
									disabled={current === 1 && !selectedDropdown}>
									{t('Patient.data.omnirom.next')}
								</Button>
							)}
							{current === steps.length - 1 && (
								<>
									<Button
										onClick={() => handleSelectAll()}
										style={{ margin: '0 10px' }}>
										{!isSelectAll
											? `${t('Admin.data.intakeProcess.selectAll')}`
											: `${t('Admin.data.intakeProcess.deselectAll')}`}
									</Button>
									<Button
										disabled={selectedItems.length === 0}
										onClick={() => handleSubmitSelected()}>
										{t('Admin.data.intakeProcess.enroll')}
									</Button>
								</>
							)}
						</div>
					</Card>
				)}

				{
					<div className="suggested-programs-container">
						<p className="my-suggested-program-container">
							<span style={{ color: 'var(--text-color-root)' }}>
								{t('Patient.data.newDashboard.suggestedPrograms')}
							</span>
							<span className="suggested-programs-tips">
								{t('Patient.data.newDashboard.suggestedProgramsTips')}
							</span>
						</p>
						<div className="image-navigation-container">
							<div
								className="horizontal-scroll-container relative"
								ref={scrollRef}>
								{suggestedProgramData?.map(
									(item: ISuggestedProgramItem, index: number) => {
										const videoUrl =
											item?.attributes?.exercise_video?.data[0]?.attributes
												?.url;
										return (
											<div
												key={index}
												className={`custom-image-container-form ${activeImage === index ? 'active-image' : ''}`}
												onMouseOver={() => handleImageClick(index, videoUrl)}
												onMouseLeave={() => setActiveImage(null)}
												onClick={e => {
													e.stopPropagation();
													navigate(`/${user?.id}/overview`, {
														state: {
															selectedProgram: item,
															domain: INTAKE_STEPS.SUGGESTED,
														},
													});
												}}>
												<div className="image-wrapper-form">
													{item?.exercises && item.exercises[0] ? (
														<img
															src={
																item?.thumbnail
																	? typeof item.thumbnail === 'string'
																		? item.thumbnail
																		: item.thumbnail.url
																	: item?.exercises?.[0]?.strapiExerciseId
																			?.exercise_image?.[0]?.url
															}
															alt={item?.exercises[0]?.strapiExerciseId?.name}
															className="program-image-container-custom-class saturate-image"
														/>
													) : (
														<video
															preload="metadata"
															src={
																item?.exercises![0]?.exerciseLibrary?.videoUrl
															}
															className="program-image-container-custom-class"
														/>
													)}
													<div className="overlay-content-css">
														<p
															className={`program-text-container-custom-class ${activeImage === index ? 'mb-2' : ''}`}>
															{item?.name}
														</p>
														<Button
															className="start-session-css start-session-btn-additional-css"
															onClick={e => {
																e.stopPropagation();
																handleEnroll(item);
															}}>
															{t('Patient.data.newDashboard.enroll')}
														</Button>

														<div className="hover-details">
															{item?.exercises && (
																<>
																	<div className="exercise-count">
																		{`${item?.exercises.length} ${t(
																			`Patient.data.newDashboard.${item?.exercises.length === 1 ? 'exercise' : 'exercises'}`,
																		)}`}
																	</div>
																	<div className="exercise-count-duration">
																		<Calendar height={20} width={20} />{' '}
																		{item?.duration}{' '}
																		{item?.durationType
																			? item?.duration > 1
																				? item.durationType
																						.charAt(0)
																						.toUpperCase() +
																					item.durationType.slice(1)
																				: item.durationType
																						.charAt(0)
																						.toUpperCase() +
																					item.durationType
																						.slice(1)
																						.replace(/s$/, '')
																			: ''}
																	</div>
																</>
															)}
														</div>
													</div>
												</div>
											</div>
										);
									},
								)}
							</div>
							<div className="navigation-buttons">
								<span className="nav-button left" onClick={scrollLeft}>
									<ChevronLeft width={30} height={30} />
								</span>
								<span className="nav-button right" onClick={scrollRight}>
									<ChevronRight width={30} height={30} />
								</span>
							</div>
						</div>
					</div>
				}
			</div>
			{suggestedProgramModal && (
				<Modal
					open={suggestedProgramModal}
					onCancel={() => setSuggestedProgramModal(false)}
					footer={null}
					width={1200}
					centered
					destroyOnClose>
					<div style={{ padding: '1rem' }}>
						<SelectedProgram state={{ state: selectedProgramModalData }} />
					</div>
				</Modal>
			)}
		</>
	);
}
